import { useState } from "react";
import { Select, MenuItem, FormControl, InputLabel, SelectChangeEvent } from "@mui/material";
import { NotesConfirmDialog } from "./NotesConfirmDialog";
import { CancelledNotesDialog } from "./CancelledNotesDialog";
import { FormatedField } from "../../../types/fields.types";
import { useUpdateRecord } from "../../../api/useUpdateRecord";
import { CalendarEvent } from "../../../components/calendar/calendar.types";
import { Session } from "../../../types/records.types";
import { DoStuffAfterReturnType } from "../../../types/groups.types";
import { useStore } from "../../../store/store";
import { Notifications } from "../../../store/store.types";
import { deleteGMeeting } from "../sessionsOptions";
import { useUpdateSessionsSummaryCache } from "../../../api/client-sessions-summary";

type SessionStatusFieldProps = {
    field: FormatedField;
    value: string;
    event: CalendarEvent | undefined;
    session: Session;
    setPreviewIsOpen: (value: boolean) => void;
    refetchSessions: () => void;
};

export const SessionStatusField = ({
    field,
    value,
    event,
    session,
    setPreviewIsOpen,
    refetchSessions,
}: SessionStatusFieldProps) => {
    const [notesDialogOpen, setNotesDialogOpen] = useState(false);
    const [cancelledNotesDialogOpen, setCancelledNotesDialogOpen] = useState(false);
    const [pendingEvent, setPendingEvent] = useState<SelectChangeEvent | null>(null);
    const updateStatus = useUpdateRecord();
    const { notifications } = useStore();
    const { updateSessionsSummaryCache } = useUpdateSessionsSummaryCache();

    const onSelectChange = (changeEvent: SelectChangeEvent) => {
        const statusValue = changeEvent.target.value;
        const isWaitingInClinic = statusValue === "awaiting_in_clinic";
        const isCancelled = statusValue === "cancelled" || statusValue === "staff_unavailable";
        const hasNotes = !!session.notes;

        if (isCancelled) {
            setPendingEvent(changeEvent);
            setCancelledNotesDialogOpen(true);
            return;
        }

        if (isWaitingInClinic && hasNotes) {
            setPendingEvent(changeEvent);
            setNotesDialogOpen(true);
            return;
        }

        handleChange(changeEvent, notifications, deleteGMeeting, session.meeting_id);
    };

    const onNotesDialogConfirm = () => {
        if (pendingEvent) {
            handleChange(pendingEvent, notifications, deleteGMeeting, session.meeting_id);
        }
        setNotesDialogOpen(false);
        setPendingEvent(null);
    };

    const onNotesDialogCancel = () => {
        setNotesDialogOpen(false);
        setPendingEvent(null);
    };

    const onCancelledNotesDialogConfirm = (notes: string) => {
        if (pendingEvent) {
            handleChange(pendingEvent, notifications, deleteGMeeting, session.meeting_id, notes);
        }
        setCancelledNotesDialogOpen(false);
        setPendingEvent(null);
    };

    const onCancelledNotesDialogCancel = () => {
        setCancelledNotesDialogOpen(false);
        setPendingEvent(null);
    };

    const handleChange = async (
        changeEvent: SelectChangeEvent,
        notifications: Notifications,
        deleteGMeeting: (meetingId: string) => Promise<DoStuffAfterReturnType | undefined>,
        meetingId?: string,
        notes?: string
    ) => {
        const eventId = event?.id;
        const statusValue = changeEvent.target.value;
        const isSessionCancelled = statusValue === "cancelled" || statusValue === "staff_unavailable" || statusValue === "missed";

        const updateData: { status: string; notes?: string } = { status: statusValue };
        if (notes) {
            updateData.notes = notes;
        }

        try {
            const response = await updateStatus.mutateAsync({
                entity: "sessions",
                id: eventId,
                data: updateData,
            });

            if (!response.id) return;

            setPreviewIsOpen(false);

            const sessionData = session as any;
            updateSessionsSummaryCache({
                clientId: sessionData?.treatment?.client?.id,
                serviceId: sessionData?.service?.id,
                oldStatus: value,
                newStatus: statusValue,
            });

            refetchSessions();

            if (isSessionCancelled && eventId && meetingId) {
                const response = await deleteGMeeting(meetingId);
                notifications.setNotification({
                    isOpen: true,
                    message: response?.message,
                    severity: response?.severity,
                });
            }
        } catch (error: any) {
            notifications.setNotification({
                isOpen: true,
                message: error.response.data.errors[0].message,
                severity: "error",
            });
        }
    };

    return (
        <>
            <FormControl fullWidth>
                <InputLabel>Estado da sessão</InputLabel>
                <Select
                    value={value || ""}
                    onChange={onSelectChange}
                    label="Estado da sessão"
                    sx={{ height: 56 }}
                >
                    {field.settings?.options?.choices?.map((choice) => {
                        return (
                            <MenuItem key={choice.value} value={choice.value} disabled={checkIfStatusChangeAllowed(value, choice.value)}>
                                {choice.text}
                            </MenuItem>
                        );
                    })}
                </Select>
            </FormControl>

            <NotesConfirmDialog
                isOpen={notesDialogOpen}
                notes={session.notes || ""}
                onConfirm={onNotesDialogConfirm}
                onCancel={onNotesDialogCancel}
            />

            <CancelledNotesDialog
                isOpen={cancelledNotesDialogOpen}
                onConfirm={onCancelledNotesDialogConfirm}
                onCancel={onCancelledNotesDialogCancel}
            />
        </>
    );
};


const SESSION_STATUS = ["booked", "confirmed", "awaiting_in_clinic", "in_session", "done", "cancelled", "staff_unavailable", "missed"] as const
const checkIfStatusChangeAllowed = (currentSatus: any, targetStatus: any) => {
	if (currentSatus === targetStatus) return true
	const free_status = SESSION_STATUS.slice(5, 8)
	if (targetStatus === "staff_unavailable") return false
	if (free_status.includes(targetStatus)) return currentSatus === "in_session" || currentSatus === "done"

	const sequential_status = SESSION_STATUS.slice(0, 5)
	const currIndex = sequential_status.indexOf(currentSatus)
	const targetIndex = sequential_status.indexOf(targetStatus)
	if (currIndex === -1) return false

	const isDisable = targetIndex > currIndex + 1
	return isDisable

}
